import Datetime from 'utils/datetime/datetime'

//TODO: Move magic numbers to general file
export const CM_MARKET_CLOSE_HOUR = 7
export const CM_MARKET_CLOSE_MINUTE = 30
export const EAM_INTERVAL_MINUTES = 15
export const EAM_GATE_CLOSURE_LEAD_MINUTES = 45
export const MINUTE_IN_MS = 60 * 1000

export type MarketCloseCountdown = {
  hours: number
  minutes: number
  hasPassed: boolean
}

export type TimeComponents = {
  hours: number
  minutes: number
}

function getStartOfLocalDayTimestamp(timestamp: number): number {
  const date = new Date(timestamp)
  date.setHours(0, 0, 0, 0)
  return date.getTime()
}

function applyInputMarginToTimestamp(timestamp: number, inputMarginMinutes?: number): number {
  if (inputMarginMinutes === undefined || inputMarginMinutes <= 0) {
    return timestamp
  }

  return timestamp - inputMarginMinutes * MINUTE_IN_MS
}

export function parseTimeString(timeStr: string): TimeComponents {
  const [hoursStr, minutesStr] = timeStr.split(':')
  const hours = parseInt(hoursStr, 10)
  const minutes = parseInt(minutesStr, 10)
  return {
    hours: Number.isNaN(hours) ? CM_MARKET_CLOSE_HOUR : hours,
    minutes: Number.isNaN(minutes) ? CM_MARKET_CLOSE_MINUTE : minutes,
  }
}

function getCMMarketCloseTimestamp(
  reference: number,
  dayOffset = 0,
  hour = CM_MARKET_CLOSE_HOUR,
  minute = CM_MARKET_CLOSE_MINUTE
): number {
  const close = new Date(reference)
  close.setDate(close.getDate() + dayOffset)
  close.setHours(hour, minute, 0, 0)
  return close.getTime()
}

export function getNextCMMarketCloseTimestamp(
  now: number,
  timeComponents?: TimeComponents,
  cmInputMarginClosureTime?: number
): number {
  const hour = timeComponents?.hours ?? CM_MARKET_CLOSE_HOUR
  const minute = timeComponents?.minutes ?? CM_MARKET_CLOSE_MINUTE
  const todayClose = applyInputMarginToTimestamp(
    getCMMarketCloseTimestamp(now, 0, hour, minute),
    cmInputMarginClosureTime
  )

  if (now >= todayClose) {
    return applyInputMarginToTimestamp(getCMMarketCloseTimestamp(now, 1, hour, minute), cmInputMarginClosureTime)
  }

  return todayClose
}

export function hasCMMarketClosedForDeliveryDate(
  deliveryDate: ISODateTime | number,
  now: number,
  timeComponents?: TimeComponents,
  cmInputMarginClosureTime?: number,
  cmLeadDays = 1
): boolean {
  if (!timeComponents) {
    return false
  }

  const hour = timeComponents.hours
  const minute = timeComponents.minutes
  const leadDays = Math.max(0, cmLeadDays)
  const productDayStart = getStartOfLocalDayTimestamp(new Date(deliveryDate).getTime())
  const closeTimestamp = applyInputMarginToTimestamp(
    getCMMarketCloseTimestamp(productDayStart, -leadDays, hour, minute),
    cmInputMarginClosureTime
  )
  return now >= closeTimestamp
}

export function hasEAMMarketClosedForDeliveryRow(
  rowStartTime: ISODateTime | number,
  now: number,
  eamGateClosureMinutesBefore?: number,
  eamInputMarginClosureTime?: number
): boolean {
  const rowStartMs = typeof rowStartTime === 'number' ? rowStartTime : new Date(rowStartTime).getTime()
  const leadMinutes = eamGateClosureMinutesBefore ?? EAM_GATE_CLOSURE_LEAD_MINUTES
  const inputMarginMinutes = eamInputMarginClosureTime ?? 0
  const totalLeadMinutes = leadMinutes + inputMarginMinutes

  return now >= rowStartMs - totalLeadMinutes * MINUTE_IN_MS
}

export function hasEAMMarketClosedForDeliveryDate(
  deliveryDate: ISODateTime | number,
  now: number,
  eamGateClosureMinutesBefore?: number,
  eamInputMarginClosureTime?: number
): boolean {
  const deliveryDateStart = new Date(deliveryDate)
  deliveryDateStart.setHours(23, 60 - EAM_INTERVAL_MINUTES, 0, 0)
  const lastDeliveryRowStart = deliveryDateStart.getTime()

  return hasEAMMarketClosedForDeliveryRow(
    lastDeliveryRowStart,
    now,
    eamGateClosureMinutesBefore,
    eamInputMarginClosureTime
  )
}

export function isMfrrDateTooFarInFuture(
  deliveryDate: ISODateTime | number,
  now: number,
  maxBidDaysAhead?: number | null
): boolean {
  if (maxBidDaysAhead === undefined || maxBidDaysAhead === null) {
    return false
  }

  const effectiveMaxBidDaysAhead = Math.max(0, maxBidDaysAhead)

  const deliveryDateStart = new Date(deliveryDate)
  deliveryDateStart.setHours(0, 0, 0, 0)

  const todayStart = new Date(now)
  todayStart.setHours(0, 0, 0, 0)

  const maxAllowedDate = new Date(todayStart)
  maxAllowedDate.setDate(maxAllowedDate.getDate() + effectiveMaxBidDaysAhead)

  return deliveryDateStart.getTime() > maxAllowedDate.getTime()
}

export function getNextEAMMarketCloseTimestamp(now: number, eamInputMarginClosureTime?: number): number {
  const intervalMs = EAM_INTERVAL_MINUTES * 60 * 1000
  const marginMs = (eamInputMarginClosureTime ?? 0) * MINUTE_IN_MS
  const adjustedNow = now + marginMs
  const nextMarketClose = Math.floor(adjustedNow / intervalMs) * intervalMs + intervalMs
  return nextMarketClose - marginMs
}

export function getEAMClosingWindowStartTimestamp(
  marketCloseTimestamp: number,
  eamGateClosureMinutesBefore?: number,
  eamInputMarginClosureTime?: number
): number {
  const marginMs = (eamInputMarginClosureTime ?? 0) * MINUTE_IN_MS
  const intervalBoundaryTimestamp = marketCloseTimestamp + marginMs
  const leadMinutes = eamGateClosureMinutesBefore ?? EAM_GATE_CLOSURE_LEAD_MINUTES
  const start = Datetime.addMinutes(Datetime.toISOString(intervalBoundaryTimestamp), leadMinutes)
  return new Date(start).getTime()
}

export function getEAMClosingWindowEndTimestamp(
  marketCloseTimestamp: number,
  eamGateClosureMinutesBefore?: number,
  eamInputMarginClosureTime?: number
): number {
  const marginMs = (eamInputMarginClosureTime ?? 0) * MINUTE_IN_MS
  const intervalBoundaryTimestamp = marketCloseTimestamp + marginMs
  const leadMinutes = eamGateClosureMinutesBefore ?? EAM_GATE_CLOSURE_LEAD_MINUTES
  const leadToWindowEnd = leadMinutes + EAM_INTERVAL_MINUTES
  const end = Datetime.addMinutes(Datetime.toISOString(intervalBoundaryTimestamp), leadToWindowEnd)
  return new Date(end).getTime()
}

export function getMarketCloseCountdown(marketCloseTimestamp: number, now: number): MarketCloseCountdown {
  const secondsUntilClose = Math.max(0, Math.floor((marketCloseTimestamp - now) / 1000))
  return {
    hours: Math.floor(secondsUntilClose / 3600),
    minutes: Math.floor((secondsUntilClose % 3600) / 60),
    hasPassed: now >= marketCloseTimestamp,
  }
}

export function getRelativeDayLabel(timestamp: number, now: number, t: (key: string) => string): string {
  const targetDate = new Date(timestamp)
  const nowDate = new Date(now)

  targetDate.setHours(0, 0, 0, 0)
  const targetDay = targetDate.getTime()

  const todayStart = new Date(nowDate)
  todayStart.setHours(0, 0, 0, 0)
  const todayMs = todayStart.getTime()

  const tomorrowMs = todayMs + 24 * 60 * 60 * 1000

  if (targetDay === todayMs) return t('Today')
  if (targetDay === tomorrowMs) return t('Tomorrow')
  return Datetime.toLocalTime(Datetime.toISOString(timestamp), 'onlyDate')
}

export function getCMDeliveryDateStartTime(cmMarketCloseTimestamp: number, cmLeadDays: number): ISODateTime {
  const deliveryMs = cmMarketCloseTimestamp + Math.max(0, cmLeadDays) * 24 * 60 * 60 * 1000
  return Datetime.getStartOfDay(Datetime.toISOString(deliveryMs))
}

/** `useSyncExternalStore` subscriber that notifies on every minute boundary. */
export function subscribeToMinuteBoundary(onStoreChange: () => void): () => void {
  let interval: ReturnType<typeof setInterval> | undefined
  const millisecondsUntilNextMinute = 60_000 - (Date.now() % 60_000)
  const timeout = setTimeout(() => {
    onStoreChange()
    interval = setInterval(onStoreChange, 60_000)
  }, millisecondsUntilNextMinute)

  return () => {
    clearTimeout(timeout)
    if (interval) {
      clearInterval(interval)
    }
  }
}

/** `useSyncExternalStore` snapshot: the current minute index since epoch. */
export function getCurrentMinuteMarker(): number {
  return Math.floor(Date.now() / 60_000)
}
